/**
 * Decision to execution — L3 of the Decision Latency PRD. Pure arithmetic.
 *
 * PRD: docs/9-exploration/DECISION-LATENCY-INSTRUMENTATION-PRD.md §5.
 * Owner ruling (2026-10-14): "execution started" is the first time a
 * commitment on the authorized plan reaches the committed state — the
 * commitments suite's announced → committed reading (commitmentState.ts).
 *
 * The committed state is DERIVED, never stored (commitmentState.ts header), so
 * there is no stamp that says "this became committed on Tuesday". What IS
 * stored is every resource claim with its own timestamps: `created_at` (it
 * starts binding), `withdrawn_at` (it stops), and supersession (it stops when
 * the claim replacing it is created). This module replays those events in time
 * order and asks the one existing judgment — `deriveCommitmentState` — at each
 * moment. The first moment it answers `committed` is execution start. No second
 * copy of the coverage math exists anywhere.
 *
 * Honesty rules, same as the rest of the latency report:
 *  - A commitment with no requirement basis (no work type, no recipe, or a
 *    recipe with no demand template) can never read `committed`; it is counted
 *    separately as "can't be seen", never as "not started".
 *  - The requirement is today's recipe, not the recipe as it stood at the
 *    time. Recipes rarely change, and the alternative is inventing history.
 *  - Resourced before the plan was authorized counts as starting at the
 *    decision (0 days), flagged, never as a negative duration.
 */

import {
  deriveCommitmentState,
  type CommitmentClaim,
  type ResourceRequirementLine,
} from './commitmentState';
import { percentile } from './decisionLatencyCore';

const DAY_MS = 24 * 3600_000;

/** A claim with the stamps that place it in time. */
export interface TimedClaim extends CommitmentClaim {
  id: string;
  createdAt: string;
  withdrawnAt?: string | null;
  supersededByClaimId?: string | null;
}

interface Interval {
  claim: CommitmentClaim;
  start: number;
  /** Exclusive. Infinity while it still binds. */
  end: number;
}

function toMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/** When each claim bound, from its own stamps. */
function claimIntervals(claims: readonly TimedClaim[]): Interval[] {
  const createdById = new Map(claims.map((c) => [c.id, toMs(c.createdAt)]));
  const out: Interval[] = [];
  for (const c of claims) {
    const start = toMs(c.createdAt);
    if (start === null) continue;
    let end = Infinity;
    const withdrawn = toMs(c.withdrawnAt);
    const replacedAt = c.supersededByClaimId ? createdById.get(c.supersededByClaimId) ?? null : null;
    if (withdrawn !== null) end = Math.min(end, withdrawn);
    if (replacedAt !== null) end = Math.min(end, replacedAt);
    // Ended, but nothing says when (a superseded claim whose replacement we
    // cannot see): it never counts. Claiming coverage on a guess is the lie.
    if (end === Infinity && c.status !== 'declared') continue;
    if (end <= start) continue;
    out.push({ claim: { ...c, status: 'declared' }, start, end });
  }
  return out;
}

/**
 * The first moment the commitment read `committed`, as an ISO stamp, or null
 * if it never has. Replays claim history through `deriveCommitmentState`.
 */
export function firstCommittedAt(
  requirements: readonly ResourceRequirementLine[],
  claims: readonly TimedClaim[],
  threshold?: number,
): string | null {
  const intervals = claimIntervals(claims);
  const moments = [...new Set(intervals.flatMap((i) => [i.start, i.end]).filter(Number.isFinite))].sort(
    (a, b) => a - b,
  );
  for (const t of moments) {
    const live = intervals.filter((i) => i.start <= t && t < i.end).map((i) => i.claim);
    if (live.length === 0) continue;
    const view = deriveCommitmentState({ requirements, claims: live, threshold });
    if (view.state === 'committed') return new Date(t).toISOString();
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per cycle
// ---------------------------------------------------------------------------

export interface CommitmentExecutionInput {
  itemId: string;
  title: string | null;
  /** When the authorized plan carrying this commitment was first authorized. */
  decidedAt: string;
  requirements: readonly ResourceRequirementLine[];
  claims: readonly TimedClaim[];
}

export interface CommitmentExecution {
  itemId: string;
  title: string | null;
  decidedAt: string;
  /** Null when it has not started, or cannot be seen to start. */
  startedAt: string | null;
  /** Whole and fractional days; null when not started. */
  daysToStart: number | null;
  /** Resources were bound before the plan was authorized; counted as 0 days. */
  resourcedBeforeDecision: boolean;
  /** No requirement to measure against, so `committed` can never be read. */
  noResourceBasis: boolean;
}

export interface CycleExecutionInput {
  planCycleId: string;
  name: string;
  /** First authorization of the cycle's plan. */
  authorizedAt: string;
  commitments: readonly CommitmentExecutionInput[];
  threshold?: number;
}

export interface CycleExecution {
  planCycleId: string;
  name: string;
  authorizedAt: string;
  /** Commitments on the authorized plan — the decisions made. */
  decided: number;
  /** Of those, how many reached the committed state. */
  started: number;
  /** Of those not started, how many could never be seen to start. */
  noResourceBasis: number;
  /** started / decided; null when nothing was decided. */
  ratio: number | null;
  medianDaysToStart: number | null;
  commitments: CommitmentExecution[];
}

export function buildCommitmentExecution(
  input: CommitmentExecutionInput,
  threshold?: number,
): CommitmentExecution {
  const noResourceBasis = !input.requirements.some((r) => r.resourceType && r.hours > 0);
  const committedAt = noResourceBasis ? null : firstCommittedAt(input.requirements, input.claims, threshold);
  const decided = toMs(input.decidedAt);
  const committed = toMs(committedAt);
  let startedAt: string | null = null;
  let daysToStart: number | null = null;
  let resourcedBeforeDecision = false;
  if (committed !== null && decided !== null) {
    resourcedBeforeDecision = committed < decided;
    startedAt = resourcedBeforeDecision ? input.decidedAt : committedAt;
    daysToStart = resourcedBeforeDecision ? 0 : (committed - decided) / DAY_MS;
  }
  return {
    itemId: input.itemId,
    title: input.title,
    decidedAt: input.decidedAt,
    startedAt,
    daysToStart,
    resourcedBeforeDecision,
    noResourceBasis,
  };
}

export function buildCycleExecution(input: CycleExecutionInput): CycleExecution {
  const commitments = input.commitments.map((c) => buildCommitmentExecution(c, input.threshold));
  const startedDays = commitments
    .map((c) => c.daysToStart)
    .filter((d): d is number => d !== null);
  const decided = commitments.length;
  const started = startedDays.length;
  return {
    planCycleId: input.planCycleId,
    name: input.name,
    authorizedAt: input.authorizedAt,
    decided,
    started,
    noResourceBasis: commitments.filter((c) => c.startedAt === null && c.noResourceBasis).length,
    ratio: decided > 0 ? started / decided : null,
    medianDaysToStart: percentile(startedDays, 0.5),
    commitments,
  };
}

// ---------------------------------------------------------------------------
// Trend
// ---------------------------------------------------------------------------

export interface ExecutionTrend {
  /** Oldest authorization first. Cycles that decided nothing are left out. */
  cycles: CycleExecution[];
  /** One plain sentence; never says more than the data supports. */
  sentence: string;
}

function pct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

export function formatDays(days: number | null): string {
  if (days === null) return '—';
  if (days < 1) return 'under a day';
  const rounded = Math.round(days);
  return `${rounded} day${rounded === 1 ? '' : 's'}`;
}

export function buildExecutionTrend(cycles: readonly CycleExecution[]): ExecutionTrend {
  const ordered = cycles
    .filter((c) => c.decided > 0)
    .slice()
    .sort((a, b) => (toMs(a.authorizedAt) ?? 0) - (toMs(b.authorizedAt) ?? 0));

  if (ordered.length === 0) {
    return { cycles: [], sentence: 'No cycle has an authorized plan yet, so nothing has been decided to start.' };
  }
  const last = ordered[ordered.length - 1];
  const lastLine = `${last.started} of ${last.decided} commitments on ${last.name} have started`;
  const lastDays =
    last.medianDaysToStart !== null ? `, ${formatDays(last.medianDaysToStart)} after the decision (median)` : '';
  if (ordered.length === 1) {
    return {
      cycles: ordered,
      sentence: `${lastLine}${lastDays}. A trend needs a second cycle with an authorized plan.`,
    };
  }
  const first = ordered[0];
  const span = `over ${ordered.length} cycles`;
  const share =
    first.ratio !== null && last.ratio !== null
      ? first.ratio === last.ratio
        ? `The share started held at ${pct(last.ratio)} ${span}`
        : `The share started went from ${pct(first.ratio)} to ${pct(last.ratio)} ${span}`
      : '';
  const days =
    first.medianDaysToStart !== null && last.medianDaysToStart !== null
      ? `; time to start went from ${formatDays(first.medianDaysToStart)} to ${formatDays(last.medianDaysToStart)}`
      : '';
  return { cycles: ordered, sentence: `${share}${days}.` };
}
